import moment from "moment";
import {
  convertSecondsToHM,
  formatDistance,
  formatHours,
  formatSpeed,
} from "./formatter";

// 🔹 Safer + faster nested value getter
const getNestedValue = (obj, path) =>
  path?.split(".")?.reduce((acc, key) => (acc ? acc[key] : undefined), obj);

// 🔹 Render details block (memoized)
const renderDetailsBlock = (row, detailConfig, header) => {
  const data = getNestedValue(row, header?.keyName);
  const value = header?.isObjectValue ? data : row;
  const name = value?.[detailConfig?.name] ?? "--";
  const lastname = value?.[detailConfig?.lastname] ?? "";
  const code = value?.[detailConfig?.code]
    ? `(${value?.[detailConfig?.code]})`
    : "";

  return `${name} ${lastname} ${code}`.trim();
};

// 🔹 Memoized formatter
export const formatCellValue = (() => {
  const cache = new WeakMap(); // Cache results per row

  return (row, header, index) => {
    if (!row || !header) return "N/A";

    // 🔹 Check cache
    if (!cache.has(row)) {
      cache.set(row, new Map());
    }
    const rowCache = cache.get(row);
    const key = `${header.keyName}_${index}`;
    if (rowCache.has(key)) {
      return rowCache.get(key);
    }

    const value = getNestedValue(row, header.keyName);
    const value2 = getNestedValue(row, header.keyName2);
    let result;
    if (value === "N/A") return "N/A";

    switch (true) {
      case header.keyName === "sNo":
        result = index + 1;
        break;

      case header.isDetails && Array.isArray(header.showAllDeatails):
        result = renderDetailsBlock(row, header.showAllDeatails[0], header);
        break;

      case header.isLoopShowName:
        result = Array.isArray(value)
          ? value
              .map((item) => (typeof item === "object" ? item?.name : item))
              .filter(Boolean)
              .join(", ")
          : "--";
        break;

      case header.isLocation:
        result = row?.location ? row?.location : "---.---";
        break;

      case header.isAddress && !header.isLocation:
        const address =
          row?.address ||
          `${row[header.lat] ?? "--"} , ${row[header?.lng] ?? "--"}`;
        result = value ? value : address;

        break;

      case header.isBox:
        result = value;
        break;

      case header.isDate:
        result = value
          ? moment(new Date(value)).format(
              header.isSeconde
                ? "DD-MM-YYYY HH:mm:ss"
                : header.isNoTime
                  ? "DD-MM-YYYY"
                  : "DD-MM-YYYY HH:mm",
            )
          : "---";
        break;
      case header.isratio:
        result = value ? `${value2} / ${value}` : "---";
        break;

      case header.isVendorDetails:
        if (row?.vendorDTOV2.length > 0) {
          result = row.vendorDTOV2
            .map((detailConfig) => {
              return `${detailConfig.name} (${detailConfig.vehicleCount})`.trim();
            })
            .join(" , ");
        } else {
          result = `N/A`;
        }

        break;

      case header.showsiteCategory:
        result = value ? (value2 ? `${value} (${value2})` : value) : "---";
        break;

      case header.isBooleanValue:
        result = value ? "Yes" : "No";
        break;

      case header.isLoadStatus:
        result = value ? value : "NO_DATA";
        break;
      case header.isAlert:
        result = value === "POWER_CUT" ? "DISCONNECTION_ALERT" : value;
        break;

      case header.isDistance:
        result = value ? formatDistance(value, "km") : "---";
        break;

      case header.isFormatHours:
        result = value ? formatHours(value) : "0 Hr 0 Min";
        break;

      case header.isSeconde:
        result = value ? convertSecondsToHM(value) : "0 Hr 0 Min";
        break;

      case header.isStatus:
        result = value
          ? value === true
            ? "TRACKING"
            : "NO TRACKING"
          : "NO TRACKING";
        break;

      case header.isSpeed:
        result = value ? formatSpeed(value, "kmh") : "--";
        break;
      case header.isDistanceValue:
        result = value ? parseFloat(value) : "--";
        break;

      case header.isIgnition:
        result = value === true ? "ON" : value === false ? "OFF" : "--";
        break;
      case header.isPower:
        result = value === true ? "ON" : value === false ? "OFF" : "--";
        break;

      case header.isActive:
        result =
          value === true ? "Active" : value === false ? "Inactive" : "--";
        break;

      default:
        result = value ?? "--";
    }

    // 🔹 Save in cache
    rowCache.set(key, result);
    return result;
  };
})();
